import { createFileRoute } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { LegalPage, P, UL, H3 } from "@/components/marketing/LegalPage";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — banana.cards" },
      { name: "description", content: "How Banana Cards collects, uses, stores, and protects your personal data." },
      { property: "og:title", content: "Privacy Policy — banana.cards" },
      { property: "og:description", content: "Read the Banana Cards Privacy Policy." },
    ],
    links: alternateLinkList("/privacy", "en"),
  }),
  component: PrivacyPage,
});

export function PrivacyPage() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Privacy"
      italic="Policy."
      updated="November 2025"
      intro="At Banana Cards we are committed to protecting your personal data and respecting your privacy. This policy explains how we collect, use, disclose, store, and protect your information when you use our products and services."
      related={[
        { label: "Terms of Service", to: "/terms" },
        { label: "Legal Guidelines", to: "/legal-guidelines" },
        { label: "Contact us", to: "/contact" },
      ]}
      sections={[
        {
          id: "who",
          title: "Who We Are & How to Contact Us",
          body: (
            <>
              <P>Six Studio Creative FZCO is the data controller responsible for your personal information.</P>
              <P>If you have any questions or concerns about this Privacy Policy or how we handle your data, please contact us at <a href="mailto:hello@banana.cards" className="font-bold underline-offset-4 hover:underline">hello@banana.cards</a>.</P>
            </>
          ),
        },
        {
          id: "info",
          title: "Information We Collect",
          body: (
            <>
              <H3>Information You Provide Directly</H3>
              <UL items={[
                "Full name",
                "Email address",
                "Phone number",
                "Company name, job title, and industry",
                "Billing and delivery address",
                "Account login credentials",
                "Profile content you choose to publish (bio, links, images, videos)",
                "Communications with us (support, feedback, messages)",
              ]} />
              <H3>Payment Information</H3>
              <P>Payments are processed by third-party payment providers. We do not store full payment card details. We may receive limited transaction references necessary for accounting and support.</P>
              <H3>Automatically Collected Information</H3>
              <UL items={[
                "IP address",
                "Device type, browser type, and operating system",
                "Usage data (pages visited, features used, interaction timestamps)",
                "Referral sources",
                "Cookie and tracking data",
              ]} />
              <H3>Third-Party & Attribution Data</H3>
              <P>If you interact with Banana Cards via affiliate links, QR codes, or NFC cards, we may collect attribution and engagement data to ensure correct functionality, analytics, and commission tracking.</P>
              <H3>Children's Data</H3>
              <P>Banana Cards services are not intended for children, and we do not knowingly collect personal data from individuals under the age required by applicable law.</P>
            </>
          ),
        },
        {
          id: "legal-basis",
          title: "Legal Basis for Processing",
          body: (
            <>
              <P>We process your personal data based on one or more of the following legal grounds:</P>
              <UL items={[
                "Contractual necessity — to provide products and services you request",
                "Consent — where you have explicitly agreed (e.g. marketing communications)",
                "Legal obligations — including accounting, tax, and regulatory requirements",
                "Legitimate interests — improving services, security, analytics, fraud prevention",
              ]} />
            </>
          ),
        },
        {
          id: "use",
          title: "How We Use Your Information",
          body: (
            <UL items={[
              "Create and manage your Banana Cards account",
              "Provide, operate, and improve our products and services",
              "Process orders, deliveries, activations, and payments",
              "Enable NFC, QR, profile sharing, CRM, and analytics features",
              "Communicate with you regarding support, updates, or service notices",
              "Send marketing communications where permitted",
              "Maintain platform security and prevent misuse",
              "Comply with legal and regulatory obligations",
            ]} />
          ),
        },
        {
          id: "sharing",
          title: "How We Share Your Information",
          body: (
            <>
              <P><strong>We do not sell your personal data.</strong></P>
              <P>We may share limited personal information only when necessary, including with:</P>
              <UL items={[
                "Service providers (hosting, payment processors, analytics, email delivery)",
                "Logistics and fulfilment partners for hardware delivery",
                "Marketing and advertising platforms in aggregated or limited form",
                "Professional advisors (legal, accounting, compliance)",
                "Authorities or regulators when legally required",
                "Business transfers (e.g. merger, acquisition, or asset sale)",
              ]} />
              <P>We commit not to sell, share, or trade customer personal information with any third party for commercial purposes, except as required by law or for delivery purposes (sharing address/phone with carriers).</P>
            </>
          ),
        },
        {
          id: "cookies",
          title: "Cookies & Tracking Technologies",
          body: (
            <>
              <P>We use cookies and similar technologies to:</P>
              <UL items={[
                "Ensure website functionality and security",
                "Remember user preferences",
                "Analyse performance and usage",
                "Deliver relevant marketing (where permitted)",
              ]} />
              <P>You can manage cookie preferences through your browser. Disabling cookies may affect certain features.</P>
            </>
          ),
        },
        {
          id: "transfers",
          title: "International Data Transfers",
          body: (
            <>
              <P>Your information may be processed or stored outside your country of residence, including in jurisdictions where data protection laws may differ.</P>
              <P>Where required, we safeguard international transfers using appropriate legal mechanisms, such as Standard Contractual Clauses or equivalent protections.</P>
            </>
          ),
        },
        {
          id: "marketing",
          title: "Marketing Communications",
          body: (
            <>
              <P>We may contact you by email or other channels with product updates, offers, or relevant information where legally permitted.</P>
              <P>You can opt out at any time by clicking the unsubscribe link in emails or contacting us at <a href="mailto:hello@banana.cards" className="font-bold underline-offset-4 hover:underline">hello@banana.cards</a>.</P>
            </>
          ),
        },
        {
          id: "rights",
          title: "Your Data Protection Rights",
          body: (
            <>
              <P>Depending on your location, you may have the right to:</P>
              <UL items={[
                "Access your personal data",
                "Correct inaccurate or incomplete data",
                "Request deletion of your data",
                "Restrict or object to processing",
                "Withdraw consent at any time",
                "Request data portability",
                "Lodge a complaint with a data protection authority",
              ]} />
              <P>To exercise your rights, contact <a href="mailto:hello@banana.cards" className="font-bold underline-offset-4 hover:underline">hello@banana.cards</a> with sufficient information to verify your identity.</P>
            </>
          ),
        },
        {
          id: "security",
          title: "Security & Data Retention",
          body: (
            <>
              <P>We implement appropriate technical and organisational measures to protect your data against unauthorised access, loss, or misuse.</P>
              <P>We retain personal data only as long as necessary for the purposes outlined in this policy or as required by law.</P>
            </>
          ),
        },
        {
          id: "google-api",
          title: "Google API Services User Data Policy",
          body: (
            <>
              <P>banana-cards-calendar-sync’s use and transfer of information received from Google APIs to any other app will adhere to the <a href="https://developers.google.com/terms/api-services-user-data-policy" className="font-bold underline-offset-4 hover:underline" target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a>, including the Limited Use requirements.</P>
              <H3>Limited Use</H3>
              <P>Our app strictly complies with the Google API Services User Data Policy. We only use your Google Workspace data to provide or improve user-facing features that are prominent in our user interface. We do not sell your data, use it for serving advertisements, or allow humans to read it unless explicitly authorized by you for support or security reasons.</P>
            </>
          ),
        },
        {
          id: "changes",
          title: "Changes to This Policy",
          body: (
            <>
              <P>We may update this Privacy Policy from time to time. The latest version will always be available on our website, with the updated date shown at the top.</P>
              <P>Continued use of our services after changes take effect constitutes acceptance of the updated policy.</P>
            </>
          ),
        },
      ]}
    />
  );
}